import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSidebar } from "@/contexts/SidebarContext";
import {
  ProfileIcon,
  CompanyIcon,
  UsersManagementIcon,
} from "@/components/Icons/index";
import { ChevronDoubleRightIcon } from "@/components/Icons/ChevronDoubleRightIcon";
import { ChevronDoubleLeftIcon } from "@/components/Icons/ChevronDoubleLeftIcon";

interface NavigationItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  children?: NavigationItem[];
}

const settingsNavigationItems: NavigationItem[] = [
  {
    name: "profile",
    path: "/settings/profile",
    icon: <ProfileIcon className="w-5 h-5" />,
  },
  {
    name: "company",
    path: "/settings/company",
    icon: <CompanyIcon className="w-5 h-5" />,
  },
  {
    name: "team_management",
    path: "/settings/users-management",
    icon: <UsersManagementIcon className="w-5 h-5" />,
    children: [
      {
        name: "users",
        path: "/settings/users",
        icon: <ProfileIcon className="w-4 h-4" />,
      },
      {
        name: "roles",
        path: "/settings/roles",
        icon: <UsersManagementIcon className="w-4 h-4" />,
      },
      {
        name: "permissions",
        path: "/settings/permissions",
        icon: <UsersManagementIcon className="w-4 h-4" />,
      },
    ],
  },
];

export const TenantSettingsSidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: tenantId } = useParams();
  const { isCollapsed, toggleSidebar, isMobileOpen, closeMobileSidebar } =
    useSidebar();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName]
    );
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);
    const active = isActive(item.path);

    return (
      <div key={item.name}>
        <div
          className={`flex items-center gap-2 text-sm font-normal h-[44px] cursor-pointer transition-all duration-300 ease-in-out ${
            level === 0 ? "pl-4 pr-3 py-3" : "p-3 pl-10"
          } ${isCollapsed ? "justify-center" : ""} ${
            active
              ? "bg-[#000000]/24 text-white"
              : "text-[#CAD5E2] hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20"
          }`}
          onClick={() => {
            if (hasChildren && !isCollapsed) {
              toggleExpanded(item.name);
            } else if (!hasChildren) {
              navigate(item.path);
              closeMobileSidebar();
            }
          }}
        >
          <span className="flex-shrink-0">{item.icon}</span>
          {!isCollapsed && (
            <>
              <span className="flex-1">
                {t(`settings.navigation.${item.name}`)}
              </span>
              {hasChildren && (
                <ChevronDoubleRightIcon
                  className={`w-4 h-4 transition-transform duration-300 ease-in-out ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                />
              )}
            </>
          )}
        </div>

        {hasChildren && isExpanded && !isCollapsed && (
          <div>
            {item.children!.map((child) => (
              <div
                key={child.name}
                onClick={() => {
                  navigate(child.path);
                  closeMobileSidebar();
                }}
                className={`flex items-center gap-2 text-sm font-normal h-[44px] pl-10 pr-3 cursor-pointer transition-all duration-300 ease-in-out ${
                  isActive(child.path)
                    ? "bg-[#000000]/24 text-white"
                    : "text-[#CAD5E2] hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20"
                }`}
              >
                <span className="flex-shrink-0">{child.icon}</span>
                <span>{t(`settings.navigation.${child.name}`)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity duration-500 ease-in-out ${
          isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileSidebar}
      />

      <div
        className={`sidebar settings-sidebar fixed left-0 z-40 top-[58px] h-[calc(100vh-58px)] transition-[transform,width] duration-500 ease-in-out ${
          isCollapsed ? "w-14" : "w-[250px]"
        } max-lg:-translate-x-full max-lg:top-0 max-lg:h-screen max-lg:transition-transform max-lg:duration-500 max-lg:ease-in-out ${
          isMobileOpen ? "max-lg:translate-x-0" : ""
        }`}
        style={{
          backgroundColor: "#182836",
          backgroundImage: "none",
          backdropFilter: "none",
        }}
      >
        <div className="flex flex-col h-full">
          {/* Settings Header */}
          <div className="px-2 py-1.5 border-b border-white/20 flex items-center justify-between">
            {!isCollapsed && (
              <h2 className="text-sm font-semibold text-ds-accent px-2">
                Settings
              </h2>
            )}
            <button
              onClick={toggleSidebar}
              className="rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all p-1.5"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronDoubleRightIcon className="w-4 h-4" />
              ) : (
                <ChevronDoubleLeftIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto">
            {settingsNavigationItems.map((item) => renderNavigationItem(item))}
          </nav>

          {/* Bottom Section */}
          <div className="p-4 border-t border-white/20">
            {!isCollapsed && (
              <div className="text-xs text-gray-400 text-center">
                <div className="gradient-text font-semibold">DS Insight</div>
                <div className="text-gray-500">Settings v1.0</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
